// ============================================
// FILE: src/components/DesktopNavbar.jsx
// Shared sticky top navigation used across all desktop (>=1024px) pages.
// Provides brand, location, primary nav links, search and icon actions.
// ============================================

import React from 'react';
import { Search, X, Bell, MapPin, Heart, ShoppingCart, LogOut, User, Home, Package, RefreshCw } from 'lucide-react';

const NAV_LINKS = [
  { key: 'home', label: 'Home', Icon: Home },
  { key: 'orders', label: 'Orders', Icon: Package },
  { key: 'subscriptions', label: 'Subscriptions', Icon: RefreshCw },
];

const DesktopNavbar = ({
  appSettings,
  userProfile = {},
  activeTab,
  setActiveTab,
  searchQuery = '',
  setSearchQuery,
  selectedLocation,
  setShowLocationModal,
  notifications = [],
  favorites = [],
  getCartCount,
  setShowNotifications,
  setShowFavorites,
  handleLogout,
  isAuthenticated = true,
  onLogin,
}) => {
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const favArray = Array.isArray(favorites) ? favorites : [];
  const unreadCount = notifications.filter(n => n.unread).length;
  const cartCount = typeof getCartCount === 'function' ? getCartCount() : 0;

  const handleSearchChange = (value) => {
    if (typeof setSearchQuery === 'function') setSearchQuery(value);
    // Search only filters the product grid on Home — jump there (and clear any open
    // overlay, since setActiveTab is wired to navigate) so results are visible.
    if (value && typeof setActiveTab === 'function') {
      setActiveTab('home');
    }
  };

  return (
    <header className="sticky top-0 z-[210] bg-white rounded-b-3xl" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
      <div className="max-w-[1280px] mx-auto px-8 h-20 flex items-center gap-5">
        {/* Brand */}
        <button onClick={() => setActiveTab && setActiveTab('home')} className="flex items-center gap-3 flex-shrink-0">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm" style={{ background: '#f3f4f6' }}>
            <img src={logoSrc} alt={appSettings?.appName || 'logo'} className="w-9 h-9 rounded-xl object-contain" />
          </div>
          <h1 className="app-brand-title text-lg" style={{ color: '#1b1b1b' }}>
            {appSettings?.appName || 'DealStore'}
          </h1>
        </button>

        {/* Location */}
        <button onClick={() => setShowLocationModal && setShowLocationModal(true)} className="flex items-center gap-1.5 min-w-0 flex-shrink-0 px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors">
          <MapPin size={18} style={{ color: '#f05a23', flexShrink: 0 }} />
          <span className="text-sm font-semibold text-gray-800 truncate max-w-[150px]">{selectedLocation}</span>
          <span className="text-gray-400 text-xs">▾</span>
        </button>

        {/* Primary nav links (icon only) */}
        <nav className="hidden lg:flex items-center gap-1 flex-shrink-0">
          {NAV_LINKS.map(({ key, label, Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab && setActiveTab(key)}
                title={label}
                aria-label={label}
                className={`p-2.5 rounded-xl transition-colors ${
                  isActive ? 'text-orange-600 bg-orange-50' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon size={20} strokeWidth={isActive ? 2.4 : 1.9} />
              </button>
            );
          })}
        </nav>

        {/* Search */}
        <div className="flex-1 flex justify-center min-w-[160px]">
          <div className="w-full max-w-md xl:max-w-lg flex items-center gap-3 px-4 py-2.5 rounded-2xl" style={{ background: '#f2f2f2', border: '1.5px solid #e8e8e8' }}>
            <Search size={18} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400 min-w-0"
            />
            {searchQuery.length > 0 && (
              <button
                onClick={() => handleSearchChange('')}
                className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-300 hover:bg-gray-400 flex items-center justify-center transition-colors"
                aria-label="Clear search"
              >
                <X size={12} className="text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Icon actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => setShowNotifications && setShowNotifications(true)} className="relative p-2.5 rounded-full hover:bg-gray-100 transition-all" title="Notifications">
            <Bell size={20} className="text-gray-700" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{unreadCount}</span>
              </span>
            )}
          </button>
          <button onClick={() => setShowFavorites && setShowFavorites(true)} className="relative p-2.5 rounded-full hover:bg-gray-100 transition-all" title="Favorites">
            <Heart size={20} className="text-gray-700" />
            {favArray.length > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-pink-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{favArray.length}</span>
              </span>
            )}
          </button>
          <button onClick={() => setActiveTab && setActiveTab('cart')} className="relative p-2.5 rounded-full hover:bg-gray-100 transition-all" title="Cart">
            <ShoppingCart size={20} className="text-gray-700" />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 min-w-4 h-4 px-1 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{cartCount}</span>
              </span>
            )}
          </button>
          {isAuthenticated ? (
            <button onClick={() => setActiveTab && setActiveTab('profile')} className="flex flex-col items-center gap-0.5 ml-1 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-all" title="Profile">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <User size={16} style={{ color: '#f05a23' }} />
              </div>
              <span className="text-[11px] font-semibold text-gray-700 leading-tight max-w-[90px] truncate">
                {userProfile?.name || 'Account'}
              </span>
            </button>
          ) : (
            <button
              onClick={() => onLogin && onLogin()}
              className="ml-1 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-transform hover:scale-105 active:scale-95"
              style={{ background: 'linear-gradient(135deg,#f05a23,#f7821a)' }}
            >
              Login
            </button>
          )}
          {isAuthenticated && handleLogout && (
            <button onClick={handleLogout} className="p-2.5 rounded-full hover:bg-gray-100 transition-all" title="Logout">
              <LogOut size={20} className="text-gray-700" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default DesktopNavbar;
